import { useEffect, useState } from 'react';

/**
 * The email a sign-in is hinted to (`/login?email=...`, the Cowtrumpet demo
 * hand-off), or null when there is none or it is not email-shaped. Whitespace
 * and case are normalised, so the same address always compares equal.
 */
export const normaliseLoginHint = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const email = raw.trim().toLowerCase();
  if (email.length > 254) return null;
  // Plain addresses only: nothing that could be markup, a URL delimiter or a
  // second query parameter once it is on an authorize URL.
  return /^[^\s@<>"'`,;:\\()[\]&=?#/]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(email)
    ? email
    : null;
};

/**
 * Reads `?email=` once, keeps it in component state, and strips it from the
 * address (keeping every other query parameter, the hash and the router's
 * history state), so the address, and anything that records it, never carries
 * the prospect's email.
 */
export const useLoginHint = (): string | null => {
  const [hint] = useState(() =>
    normaliseLoginHint(new URLSearchParams(window.location.search).get('email')),
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('email')) return;
    url.searchParams.delete('email');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }, []);

  return hint;
};
